import { useId } from 'react';

import { Button } from './Button';
import type { IconName } from './Icon';
import { LinkButton } from './LinkButton';

/** One thing the bar offers: a button that acts here, or a link to where it is done. */
export type NextStepAction =
  | {
      readonly label: string;
      readonly onClick: () => void;
      readonly busy?: boolean;
      readonly icon?: IconName;
    }
  | { readonly label: string; readonly to: string; readonly icon?: IconName };

export interface NextStepBarProps {
  /** Names the region for assistive technology. Defaults to "Next step". */
  readonly label?: string;
  /** The one thing to do next. Omit it when the next move is somebody else's. */
  readonly primary?: NextStepAction;
  /** Other things that can be done now, drawn quieter. */
  readonly secondary?: readonly NextStepAction[];
  /** Where things stand, for example "Waiting for Dana Ruiz". Announced politely when it changes. */
  readonly status?: string;
  /** Why the primary action is not available. Disables it and says so, instead of a dead button. */
  readonly blockedReason?: string;
}

function ActionControl({
  action,
  variant,
}: {
  action: NextStepAction;
  variant: 'primary' | 'secondary';
}): React.JSX.Element {
  const icon = action.icon === undefined ? {} : { icon: action.icon };
  if ('to' in action) {
    return (
      <LinkButton variant={variant} to={action.to} {...icon}>
        {action.label}
      </LinkButton>
    );
  }
  return (
    <Button variant={variant} busy={action.busy ?? false} onClick={action.onClick} {...icon}>
      {action.label}
    </Button>
  );
}

/**
 * The one obvious thing to do with a record, and who it is waiting on.
 *
 * A record that has several things it could do next is a record whose user has
 * to work out which. This puts one of them forward as the primary action, keeps
 * the rest quiet beside it, and says in a sentence where the record stands when
 * the next move is not theirs ("Waiting for Dana Ruiz"). When the primary action
 * is not available it is shown disabled with the reason beside it, because a
 * disabled control with no explanation is the commonest way a screen becomes
 * unusable.
 *
 * The primary action comes first in the reading and tab order, so a keyboard
 * user reaches it before the alternatives.
 *
 * @param props see {@link NextStepBarProps}
 * @returns the bar, or nothing when there is nothing to say or do
 */
export function NextStepBar({
  label = 'Next step',
  primary,
  secondary = [],
  status,
  blockedReason,
}: NextStepBarProps): React.JSX.Element | null {
  const blockedId = useId();

  if (primary === undefined && secondary.length === 0 && status === undefined) {
    return null;
  }

  const blocked = primary !== undefined && blockedReason !== undefined;
  let primaryControl: React.JSX.Element | null = null;
  if (primary !== undefined) {
    primaryControl = blocked ? (
      <Button variant="primary" disabled aria-describedby={blockedId}>
        {primary.label}
      </Button>
    ) : (
      <ActionControl action={primary} variant="primary" />
    );
  }

  return (
    <section className="fo-next-step" aria-label={label}>
      {status === undefined && !blocked ? null : (
        <div className="fo-next-step__text">
          {status === undefined ? null : (
            <p className="fo-next-step__status" role="status">
              {status}
            </p>
          )}
          {blocked ? (
            <p className="fo-next-step__blocked" id={blockedId}>
              {blockedReason}
            </p>
          ) : null}
        </div>
      )}
      <div className="fo-next-step__actions">
        {primaryControl}
        {secondary.map((action) => (
          <ActionControl key={action.label} action={action} variant="secondary" />
        ))}
      </div>
    </section>
  );
}
